<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { contains, sectionOf, visibleNavigation } from '@/router/navigation'
import type { NavigationItem } from '@/router/navigation'
import { useAuth } from '@/stores/auth'

const route = useRoute()
const auth = useAuth()
/** Само разрешеното; техникът има свое кратко меню. */
const navigation = computed(() => visibleNavigation(auth))
const expanded = ref(new Set<string>())
const section = computed(() => sectionOf(route.name))

function isActive(item: NavigationItem): boolean {
  return contains(item, section.value)
}

/** Раздел със страници води към първата от тях; останалите са в горната лента. */
function target(item: NavigationItem): string {
  return item.tabs ? item.tabs[0].name : item.name
}

function toggle(name: string) {
  if (expanded.value.has(name)) expanded.value.delete(name)
  else expanded.value.add(name)
}

watch(section, () => {
  for (const item of navigation.value) {
    if (item.children && isActive(item)) expanded.value.add(item.name)
  }
}, { immediate: true })
</script>

<template>
  <nav class="sidebar" aria-label="Основна навигация">
    <div class="sidebar__brand">
      <i class="pi pi-th-large" aria-hidden="true" />
      <span class="sidebar__brand-name">Изолации</span>
    </div>

    <ul class="sidebar__list">
      <li v-for="item in navigation" :key="item.name" :class="{ 'sidebar__bottom': item.bottom }">
        <template v-if="item.children">
          <button
            type="button"
            class="sidebar__link sidebar__group"
            :class="{ 'sidebar__group--active': isActive(item) }"
            :aria-label="item.label"
            :title="item.label"
            :aria-expanded="expanded.has(item.name)"
            :aria-controls="`sidebar-${item.name}`"
            @click="toggle(item.name)"
          >
            <i :class="item.icon" aria-hidden="true" />
            <span class="sidebar__label">{{ item.label }}</span>
            <i :class="['pi', expanded.has(item.name) ? 'pi-chevron-down' : 'pi-chevron-right', 'sidebar__chevron']" aria-hidden="true" />
          </button>
          <ul v-show="expanded.has(item.name)" :id="`sidebar-${item.name}`" class="sidebar__submenu">
            <li v-for="child in item.children" :key="child.name">
              <RouterLink
                :to="{ name: target(child) }"
                class="sidebar__link"
                :class="{ 'sidebar__link--active': isActive(child) }"
                :aria-current="isActive(child) ? 'page' : undefined"
                :aria-label="child.label"
                :title="child.label"
              >
                <i :class="child.icon" aria-hidden="true" />
                <span class="sidebar__label">{{ child.label }}</span>
              </RouterLink>
            </li>
          </ul>
        </template>
        <RouterLink
          v-else
          :to="{ name: target(item) }"
          class="sidebar__link"
          :class="{ 'sidebar__link--active': isActive(item) }"
          :aria-current="isActive(item) ? 'page' : undefined"
          :aria-label="item.label"
          :title="item.label"
        >
          <i :class="item.icon" aria-hidden="true" />
          <span class="sidebar__label">{{ item.label }}</span>
        </RouterLink>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  overflow: auto;
  padding: 0.75rem 0.5rem;
  background: var(--p-surface-0);
  border-right: 1px solid var(--p-content-border-color);
}

.sidebar__brand {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem 0.75rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.sidebar__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.125rem;
}

.sidebar__bottom {
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--p-content-border-color);
}

.sidebar__link {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  border-radius: var(--p-content-border-radius, 6px);
  color: var(--p-text-muted-color);
  text-decoration: none;
  white-space: nowrap;
}

.sidebar__link:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.sidebar__link--active {
  background: var(--p-highlight-background);
  color: var(--p-highlight-color);
}

.sidebar__group {
  width: 100%;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.sidebar__group--active {
  color: var(--p-primary-color);
  font-weight: 600;
}

.sidebar__chevron {
  margin-left: auto;
  font-size: .65rem;
}

.sidebar__submenu {
  list-style: none;
  margin: .2rem 0 .4rem 1.1rem;
  padding: 0 0 0 .5rem;
  border-left: 1px solid var(--p-content-border-color);
  display: grid;
  gap: .125rem;
}

.sidebar__link:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: -2px;
}

@media (max-width: 1100px) {
  .sidebar__brand-name,
  .sidebar__label {
    display: none;
  }

  .sidebar__link {
    justify-content: center;
  }

  .sidebar__group { position: relative; }
  .sidebar__chevron { position: absolute; right: 0; font-size: .5rem; }
  .sidebar__submenu { margin-left: .2rem; padding-left: .2rem; }
}
</style>
